define((require) => {
    "use strict";

    const {sortBy, find, isEmpty}   = require("underscore");
    const UrlUtil                   = require("shared/util/UrlUtil");
    const Configuration             = require("shared/services/Configuration");
    const {Logger}                  = require("shared/util/Logger");
    const MathUtil                  = require("shared/util/MathUtil").default;
    const ImageLibrarySourceEnum    = require("shared/data/images/ImageLibrarySourceEnum");
    const {getAppropriateBoxSize}   = require("shared/xp/components/widgets/image/util/ImageBoxSize");


    // Default policy widths.
    const AKAMAI_DEFAULT_WIDTHS = sortBy([320, 640, 1024, 2048, 5000]);

    const logger = new Logger("ImageURLProvider");

    // Configure options (per QA request)
    const queryConfig = {

        imageResizeEnabled: Configuration.val({
            name: "imageResizeEnabled",
            type: "boolean",
            defaultValue: true
        }),

        imageManagerEnabled: Configuration.val({
            name: "imageManagerEnabled",
            type: "boolean",
            defaultValue: false
        }),

        imageManagerPolicy: Configuration.val({
            name: "imageManagerPolicy",
            type: "string",
            defaultValue: ""
        })
    };

    /**
     * Given a valid image URL and approximate size, we build a new URL with the size parameter in and can
     * be used to request the image.
     *
     * @param {String} url  The image URL
     * @param {Boolean} cloudinaryProvider The cloudinary provider flag
     * @param {width: Number, height: Number} size  The approximate image size
     * @param {String} sourceType   The source type from where the image comes from. If undefined, images comes from Creator.
     * @returns {String}    The new URL
     * @private
     */
    function getImageUrl(url, cloudinaryProvider, size, sourceType) {
        const source = checkSourceType(sourceType)
            ? ImageLibrarySourceEnum.lookup(sourceType) : ImageLibrarySourceEnum.CREATOR;
        return source.getUrl(url, size, cloudinaryProvider);
    }

    /**
     * This functions check if sourceType exists
     *
     * @param {String} sourceType
     * @returns {Boolean}  sourceTypeExists boolean flag
     */
    function checkSourceType(sourceType) {
        return sourceType && ImageLibrarySourceEnum.lookup(sourceType, true);
    }

    function getOriginalImageUrl(url) {
        return UrlUtil.prependProtocol(UrlUtil.getAbsoluteViewerApiUrl(url));
    }

    /**
     * Given a valid image URL and сrop size, we build a new URL with the crop parameters in and can
     * be used to request the crop of image.
     *
     * @param {String} url  The image URL
     * @param {ImageCrop} crop  The approximate image size
     * @param {String} sourceType   The source type from where the image comes from. If undefined, images comes from Creator.
     * @returns {String}    The new URL
     * @private
     */
    function getCropImageUrl(url, crop, sourceType) {
        const source = checkSourceType(sourceType) ? ImageLibrarySourceEnum.lookup(sourceType) : ImageLibrarySourceEnum.CREATOR;
        return source.getCropUrl ? source.getCropUrl(url, crop) : url;
    }

    /**
     * Based on the passed width, will return the smallest box size that fits the passed width.
     * @param {Number} width the bounds width
     * @returns {Number} the smallest box size that fits the passed width.
     */
    function getAkamaiBoxWidth(width) {
        return find(AKAMAI_DEFAULT_WIDTHS, (akamaiWidth) => width <= akamaiWidth);
    }

    /**
     * Returns the Akamai Image Manager URL.
     * @param {Model} model the Backbone model (either an image model or an effects model).
     * @param {Number} boundsWidth the width of the bounds
     * @returns {String} the akamai image manager url
     */
    function getAkamaiImageManagerUrl(model, boundsWidth) {
        const imagePolicy = queryConfig.imageManagerPolicy;
        const imageWidth = MathUtil.round(boundsWidth, 1);
        const urlParams = {
            imageManagerEnabled: true,
            imwidth: getAkamaiBoxWidth(imageWidth)
        };

        if (!isEmpty(imagePolicy)) {
            urlParams.impolicy = imagePolicy;
        }

        const imageUrl = UrlUtil.appendParamObject(model.get("imageUrl"), urlParams);

        logger.info(`Using the following Akamai Image url: ${imageUrl}`);

        return imageUrl;
    }

    /**
     * All static provider, used by the <tt>ImageWidgetView</tt> and by the <tt>ImageToggleEffect</tt>, for creating URLs using the scaled/optimized images.
     *
     * @class ImageURLProvider
     * @author Kim
     */
    return {
        // Return function to construct actual image src
        getOriginalImageUrl,
        /**
         * Returns an optimized or resized version of the image url, depending on the source type (see #getImageUrl)
         *
         * @param {ImageWidgetModel|ImageToggleEffect} model The model. It needs to be an <tt>ImageWidgetModel</tt> or an <tt>ImageToggleEffect</tt> model.
         * @param {Object} options The options object
         * @param {Object|Size} options.bounds The bounds, specifying a <tt>width</tt> and <tt>height</tt>.
         * @param {Object} options.imageServingUrl Sets the base url of source type images
         *
         * @return {String} The URL pointing to the original image or to the scaled one if the resize functionality is enabled
         */
        provide(model, options = {}) {
            const originalImageUrl = model.get("imageUrl");
            const sourceType = model.get("sourceType");
            const crop = model.get("crop");
            let url;

            // Placeholder image doesn't have src defined
            if (!model.get("src")) {
                return originalImageUrl;
            }

            if (checkSourceType(sourceType)) {
                // Old integrations suppurt (Scene7)
                url = options.imageServingUrl && UrlUtil.concat(options.imageServingUrl, originalImageUrl);
            } else if (crop) {
                // Original creator source cropped image
                const imageUrl = UrlUtil.prependProtocol(UrlUtil.getAbsoluteViewerApiUrl(model.get("src")));
                return getCropImageUrl(imageUrl, crop, sourceType);
            } else {
                // Regular creator image
                url = UrlUtil.getAbsoluteViewerApiUrl(originalImageUrl);
            }

            if (options.bounds) {
                const boxSize = {
                    width: model.get("originalImageWidth"),
                    height: model.get("originalImageHeight")
                };
                const appropriateBoxSize = getAppropriateBoxSize(boxSize, options.bounds);

                if (queryConfig.imageManagerEnabled) {
                    return getAkamaiImageManagerUrl(model, options.bounds.width);
                }

                // The image resize should only be available if resize is supported (viewer mode), if the imageResizeEnabled query param is set to true
                // and if resize is supported (the widget has an originalImage width and height properties set)
                if (queryConfig.imageResizeEnabled && appropriateBoxSize && model.get("supportsResize") && !(sourceType && crop)) {
                    return getImageUrl(url, options.cloudinary, appropriateBoxSize, model.get("sourceType"));
                }
            }

            if (model.get("src") && !model.get("sourceType")) {
                return getImageUrl(url, options.cloudinary);
            }

            return url || originalImageUrl;
        }
    };
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/imageWidget/ImageURLProvider.js